import React, { useState } from "react";
import { Controller } from "react-hook-form";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/24/solid";
import {
  countryOptions,
  getFilteredCountryOptions,
  getFilteredStateOptions,
  getStateOptions,
} from "../utils/locationData";
import { getGoogleMapsApiKey } from "../config/googleMapsConfig";
import { onBlurSelectWithDelay } from "../utils/dom-events";

const libraries = ["places"]; // We need 'places' for Google Places Autocomplete
const LocationFields = ({
  control,
  errors,
  // handlers for Google Autocomplete (optional)
  handleOnLoad,
  handlePlaceChanged,
  // generic field values & setters (preferred)
  address,
  setAddress,
  city,
  setCity,
  state,
  setState,
  country,
  setCountry,
  countrySearch,
  setCountrySearch,
  isCountryDropdownOpen,
  setIsCountryDropdownOpen,
  isStateDropdownOpen,
  setIsStateDropdownOpen,
  stateSearch,
  setStateSearch,
  // other
  setValue,
  onChangeLocation,
  classes = "",
  fullWidth = false,
  fieldPrefix = "primary",
}) => {
  // Determine which props to use (generic preferred, fallback to primary-xxx props)
  // Local fallbacks for dropdown open/search state when parent doesn't provide handlers
  const [localCntOpen, setLocalCntOpen] = useState(false);
  const [localCntSearch, setLocalCntSearch] = useState("");
  const [localStOpen, setLocalStOpen] = useState(false);
  const [localStateSearch, setLocalStateSearch] = useState("");

  const cntSearch = countrySearch ?? localCntSearch ?? "";
  const setCntSearch = setCountrySearch ?? setLocalCntSearch;
  const isCntOpen = isCountryDropdownOpen ?? localCntOpen;
  const setIsCntOpen = setIsCountryDropdownOpen ?? setLocalCntOpen;
  const isStOpen = isStateDropdownOpen ?? localStOpen;
  const setIsStOpen = setIsStateDropdownOpen ?? setLocalStOpen;
  const stSearch = stateSearch ?? localStateSearch ?? "";
  const setStSearch = setStateSearch ?? setLocalStateSearch;
  // Current labels for "show all" when the search text is unchanged
  const currentCountryLabel = country
    ? countryOptions.find((o) => o.value === country || o.label === country)?.label ?? ""
    : "";
  const currentStateLabel =
    state && getStateOptions(country).length > 0
      ? getStateOptions(country).find((o) => o.value === state || o.label === state)?.label ?? ""
      : "";

  // Get Google Maps API key (uses existing key from location autocomplete as fallback)
  const apiKey = getGoogleMapsApiKey();

  // Use the proper hook to load Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey,
    libraries: libraries,
  });

  return (
    <div className={`mt-2 sm:col-span-2 sm:mt-0 ${classes}`}>
      <span className="space-x-3">
        <span className="space-x-3">
          <div
            className={`grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-4 ${fullWidth ? "" : "sm:max-w-xl"}`}
          >
            {/* Street Address */}
            <div>
              <div
                className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors[`${fieldPrefix}Address`] ? "ring-red-300 ring-2" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors[`${fieldPrefix}Address`] ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"}`}
              >
                <Controller
                  name={`${fieldPrefix}Address`}
                  control={control}
                  render={({ field }) => (
                    <>
                      {isLoaded && (
                        <Autocomplete
                          onLoad={handleOnLoad}
                          onPlaceChanged={handlePlaceChanged}
                          className="w-full rounded-md"
                        >
                          <input
                            type="text"
                            id={`${fieldPrefix}-street-address`}
                            name={`${fieldPrefix}-street-address`}
                            value={address || ""}
                            onChange={(e) => {
                              setAddress && setAddress(e.target.value);
                              setValue(`${fieldPrefix}Address`, e.target.value);
                              onChangeLocation?.({
                                address: e.target.value,
                                city: city,
                                state: state,
                                country: country,
                              });
                            }}
                            placeholder="Street Address"
                            className="w-full block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6 rounded-md"
                          />
                        </Autocomplete>
                      )}
                    </>
                  )}
                />
              </div>
              {errors[`${fieldPrefix}Address`] && (
                <p className="mt-2 text-sm text-red-600">
                  {errors[`${fieldPrefix}Address`]?.message ?? errors[`${fieldPrefix}Address`]}
                </p>
              )}
            </div>

            {/* City */}
            <div>
              <div
                className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors[`${fieldPrefix}City`] ? "ring-red-300 ring-2" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors[`${fieldPrefix}City`] ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"}`}
              >
                <input
                  type="text"
                  id={`${fieldPrefix}-city-province`}
                  name={`${fieldPrefix}City`}
                  value={city || ""}
                  onChange={(e) => {
                    setCity && setCity(e.target.value);
                    onChangeLocation({
                      address: address,
                      city: e.target.value,
                      state: state,
                      country: country,
                    });
                    setValue(`${fieldPrefix}City`, e.target.value);
                  }}
                  placeholder="City/Town"
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                />
              </div>
              {errors[`${fieldPrefix}City`] && (
                <p className="mt-2 text-sm text-red-600">
                  {errors[`${fieldPrefix}City`]?.message ?? errors[`${fieldPrefix}City`]}
                </p>
              )}
            </div>

            {/* Country */}
            <div className="relative country-dropdown">
              <div
                className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors[`${fieldPrefix}Country`] ? "ring-red-300 ring-2" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors[`${fieldPrefix}Country`] ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"} hover:bg-gray-50 transition-colors`}
              >
                <input
                  type="text"
                  name={`${fieldPrefix}Country`}
                  value={
                    isCntOpen
                      ? cntSearch
                      : country
                        ? countryOptions.find(
                            (option) =>
                              option.value === country ||
                              option.label === country,
                          )?.label || ""
                        : ""
                  }
                  onChange={(e) => {
                    setCntSearch && setCntSearch(e.target.value);
                    if (!isCntOpen) {
                      setIsCntOpen && setIsCntOpen(true);
                    }
                  }}
                  onFocus={() => {
                    setIsCntOpen && setIsCntOpen(true);
                    const currentLabel = country
                      ? countryOptions.find(
                          (o) => o.value === country || o.label === country,
                        )?.label ?? ""
                      : "";
                    setCntSearch && setCntSearch(currentLabel);
                  }}
                  onBlur={(e) => onBlurSelectWithDelay(e, setIsCntOpen)}
                  placeholder="Select Country/Region"
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-3 pr-10 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                  autoComplete="off"
                />
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 h-9">
                  <ChevronDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </div>

              {isCntOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                  {(cntSearch === currentCountryLabel
                    ? countryOptions
                    : getFilteredCountryOptions(cntSearch)
                  ).map((option) => (
                    <div
                      key={option.value}
                      className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                        country === option.value
                          ? "text-indigo-600 bg-indigo-50"
                          : "text-gray-900 hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        setCountry && setCountry(option.value);
                        setValue(`${fieldPrefix}Country`, option.value);
                        onChangeLocation({
                          address: address,
                          city: city,
                          state: state,
                          country: option.value,
                        });
                        option.value !== country && setState && setState("");
                        setIsCntOpen && setIsCntOpen(false);
                        setCntSearch && setCntSearch("");
                      }}
                    >
                      <span className="block truncate">{option.label}</span>
                      {country === option.value && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                          <CheckIcon
                            className="h-5 w-5 text-indigo-600"
                            aria-hidden="true"
                          />
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {errors[`${fieldPrefix}Country`] && (
                <p className="mt-2 text-sm text-red-600">
                  {errors[`${fieldPrefix}Country`]?.message ?? errors[`${fieldPrefix}Country`]}
                </p>
              )}
            </div>

            {/* State/Province - Only show if country has states */}
            {getStateOptions(country).length > 0 && (
              <div className="relative state-dropdown">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-200 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 hover:bg-gray-50 transition-colors">
                  <input
                    type="text"
                    value={
                      isStOpen
                        ? stSearch
                        : state
                          ? getStateOptions(country).find(
                              (option) => option.value === state || option.label === state,
                            )?.label || ""
                          : ""
                    }
                    onChange={(e) => {
                      setStSearch && setStSearch(e.target.value);
                      if (!isStOpen) {
                        setIsStOpen(true);
                      }
                    }}
                    onFocus={() => {
                      setIsStOpen(true);
                      const currentLabel = state
                        ? getStateOptions(country).find(
                            (o) => o.value === state || o.label === state,
                          )?.label ?? ""
                        : "";
                      setStSearch && setStSearch(currentLabel);
                    }}
                    onBlur={(e) => onBlurSelectWithDelay(e, setIsStOpen)}
                    placeholder="Select State/Province"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-3 pr-10 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                    autoComplete="off"
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 h-9">
                    <ChevronDownIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </div>

                {isStOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {(stSearch === currentStateLabel
                      ? getStateOptions(country)
                      : getFilteredStateOptions(country, stSearch)
                    ).map(
                      (option) => (
                        <div
                          key={option.value}
                          className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                            state === option.value
                              ? "text-indigo-600 bg-indigo-50"
                              : "text-gray-900 hover:bg-gray-50"
                          }`}
                          onClick={() => {
                            setState && setState(option.value);
                            setValue(`${fieldPrefix}State`, option.value);
                            onChangeLocation({
                              address: address,
                              city: city,
                              state: option.value,
                              country: country,
                            });
                            setIsStOpen && setIsStOpen(false);
                            setStateSearch && setStateSearch("");
                          }}
                        >
                          <span className="block truncate">{option.label}</span>
                          {state === option.value && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                              <CheckIcon
                                className="h-5 w-5 text-indigo-600"
                                aria-hidden="true"
                              />
                            </span>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                )}
                {errors[`${fieldPrefix}State`] && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors[`${fieldPrefix}State`]?.message ?? errors[`${fieldPrefix}State`]}
                  </p>
                )}
              </div>
            )}
          </div>
        </span>
      </span>
    </div>
  );
};

export default LocationFields;
